import { formatDate } from "@/lib/format";
import { useState } from "react";
import { useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accountStatementsApi, type EmailStatementRequest } from "@/lib/clients-accounts";
import { DetailPage } from "@/archetypes/DetailPage";
import { StatementPdfFrame } from "@/components/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DetailModal } from "@/components/detail-modal";
import { toast } from "@/hooks/use-toast";
import { Download, Send } from "lucide-react";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

export function AccountStatementDetailPage() {
  const { id = "0", stmtId = "0" } = useParams();
  const accountId = Number(id);
  const statementId = Number(stmtId);
  const qc = useQueryClient();
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailBcc, setEmailBcc] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  const { data: stmt } = useQuery({
    queryKey: ["statement", statementId],
    queryFn: () => accountStatementsApi.get(statementId),
  });

  const regenMut = useMutation({
    mutationFn: () => accountStatementsApi.regenerate(statementId),
    onSuccess: (r) => {
      toast.success(r.warning ?? "Regenerated");
      void qc.invalidateQueries({ queryKey: ["statement", statementId] });
    },
    onError: () => toast.error("Regenerate failed"),
  });
  const emailMut = useMutation({
    mutationFn: (body: EmailStatementRequest) => accountStatementsApi.email(statementId, body),
    onSuccess: () => { toast.success("Sent"); setEmailDrawerOpen(false); },
    onError: () => toast.error("Send failed"),
  });

  return (
    <DetailPage
      title={stmt ? `Statement ${stmt.statementNo}` : "Loading…"}
      backTo={`/accounts/${accountId}/statements`}
      tabActions={
        stmt && (
          <div className="flex gap-2">
            <a href={accountStatementsApi.pdfUrl(statementId)} target="_blank" rel="noreferrer">
              <Button variant="outline" size="icon" aria-label="Download PDF" className="rounded-full h-8 w-8 bg-info/15 text-primary hover:bg-info/25">
                <Download className="h-4 w-4" />
              </Button>
            </a>
            <Button variant="outline" size="icon" aria-label="Email" className="rounded-full h-8 w-8 bg-info/15 text-primary hover:bg-info/25" onClick={() => {
              setEmailTo(stmt.recipientEmailAddress ?? "");
              setEmailCc(stmt.recipientCCEmailAddress ?? "");
              setEmailBcc("");
              setEmailMessage("");
              setEmailDrawerOpen(true);
            }}>
              <Send className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => regenMut.mutate()} disabled={regenMut.isPending}>
              Regenerate
            </Button>
          </div>
        )
      }
      tabs={stmt ? [{
        id: "preview", label: "Preview",
        content: (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div><dt className="text-xs text-muted-foreground">Period</dt><dd>{formatDate(stmt.periodFromDate)} — {formatDate(stmt.periodToDate)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Type</dt><dd>{stmt.outstandingBalTypeStmt ? "Outstanding-per-invoice" : "Running-balance"}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Opening</dt><dd>{fmt.format(stmt.openingBalance)}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Closing</dt><dd>{fmt.format(stmt.closingBalance)}</dd></div>
            </dl>
            <StatementPdfFrame src={accountStatementsApi.pdfUrl(statementId)} title={`Statement ${stmt.statementNo}`} />
          </div>
        ),
      }] : []}
      defaultTab="preview"
    >
      <DetailModal open={emailDrawerOpen} onOpenChange={setEmailDrawerOpen} title="Email statement">
        <form className="space-y-3" onSubmit={(e) => {
          e.preventDefault();
          emailMut.mutate({
            toEmailAddress: emailTo,
            ccEmailAddress: emailCc.trim() ? emailCc.trim() : null,
            bccEmailAddress: emailBcc.trim() ? emailBcc.trim() : null,
            message: emailMessage.trim() ? emailMessage.trim() : null,
          });
        }}>
          <div>
            <Label htmlFor="email-to">To</Label>
            <Input id="email-to" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="email-cc">Cc</Label>
            <Input id="email-cc" type="text" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="email-bcc">Bcc</Label>
            <Input id="email-bcc" type="text" value={emailBcc} onChange={(e) => setEmailBcc(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="email-message">Message</Label>
            <Textarea id="email-message" value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} rows={3} />
          </div>
          <Button type="submit" disabled={emailMut.isPending}>{emailMut.isPending ? "Sending…" : "Send"}</Button>
        </form>
      </DetailModal>
    </DetailPage>
  );
}
